import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { forkJoin } from 'rxjs';
import { AttendanceRosterRow, AttendanceStatusCode } from '../models/attendance.model';
import { AttendanceService } from '../services/attendance.service';
import { StudentService } from '../services/student.service';
import { Student } from '../student';
import { CLASS_GROUP_OPTIONS } from '../school-constants';

@Component({
  selector: 'app-attendance',
  templateUrl: './attendance.component.html',
  styleUrls: ['./attendance.component.css']
})
export class AttendanceComponent implements OnInit {

  readonly classOptions = [...CLASS_GROUP_OPTIONS];

  readonly statusOptions: { value: AttendanceStatusCode; label: string }[] = [
    { value: 'PRESENT', label: 'Present' },
    { value: 'ABSENT', label: 'Absent' },
    { value: 'LATE', label: 'Late' },
    { value: 'EXCUSED', label: 'Excused' }
  ];

  form: FormGroup;
  loadingRoster = false;
  saving = false;
  rosterLoaded = false;

  dataSource = new MatTableDataSource<AttendanceRosterRow>([]);
  displayedColumns: string[] = ['rollNumber', 'name', 'status'];

  constructor(
    private fb: FormBuilder,
    private studentService: StudentService,
    private attendanceService: AttendanceService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      className: ['', Validators.required],
      attendanceDate: [new Date(), Validators.required]
    });
  }

  loadRoster(): void {
    if (this.form.invalid) {
      this.snackBar.open('Choose class and date first.', 'Dismiss', { duration: 3000 });
      return;
    }
    const className = this.form.value.className as string;
    const dateStr = this.formatDate(this.form.value.attendanceDate as Date);

    this.loadingRoster = true;
    this.rosterLoaded = false;

    forkJoin({
      students: this.studentService.getAll(),
      day: this.attendanceService.getDayRecords(dateStr, className)
    }).subscribe({
      next: ({ students, day }) => {
        const byId = new Map(day.map(r => [r.studentId, r.status]));
        const inClass = (students || []).filter(
          (s: Student) => (s.className || '').trim() === className.trim() && s.id != null
        );
        const rows: AttendanceRosterRow[] = inClass.map(s => ({
          studentId: s.id as number,
          firstName: s.firstName || '',
          lastName: s.lastName || '',
          rollNumber: s.rollNumber,
          status: (byId.get(s.id as number) as AttendanceStatusCode) ?? null
        }));
        this.dataSource.data = rows;
        this.rosterLoaded = true;
        this.loadingRoster = false;
        if (!rows.length) {
          this.snackBar.open('No students in this class. Add students or pick another class.', 'Dismiss', { duration: 5000 });
        }
      },
      error: () => {
        this.loadingRoster = false;
        this.snackBar.open('Could not load roster.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  markAllPresent(): void {
    const next = this.dataSource.data.map(r => ({ ...r, status: 'PRESENT' as AttendanceStatusCode }));
    this.dataSource.data = next;
  }

  updateRowStatus(row: AttendanceRosterRow, status: AttendanceStatusCode | null | ''): void {
    row.status = status === '' || status == null ? null : status;
  }

  save(): void {
    if (!this.rosterLoaded || this.form.invalid) {
      this.snackBar.open('Load the roster first.', 'Dismiss', { duration: 3000 });
      return;
    }
    const dateStr = this.formatDate(this.form.value.attendanceDate as Date);
    const className = this.form.value.className as string;
    const records = this.dataSource.data
      .filter(r => r.status != null)
      .map(r => ({ studentId: r.studentId, status: r.status as AttendanceStatusCode }));

    if (!records.length) {
      this.snackBar.open('Set at least one status before saving.', 'Dismiss', { duration: 4000 });
      return;
    }

    this.saving = true;
    this.attendanceService
      .saveBatch({ attendanceDate: dateStr, className, records })
      .subscribe({
        next: () => {
          this.saving = false;
          this.snackBar.open('Attendance saved', 'Dismiss', { duration: 3000 });
        },
        error: () => {
          this.saving = false;
          this.snackBar.open(
            'Save failed. Ensure POST /attendance/batch exists on the server.',
            'Dismiss',
            { duration: 6000 }
          );
        }
      });
  }

  trackByStudentId(_index: number, row: AttendanceRosterRow): number {
    return row.studentId;
  }

  statusSelectValue(row: AttendanceRosterRow): string {
    return row.status != null ? row.status : '';
  }

  private formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
